import { configureStore, combineReducers } from '@reduxjs/toolkit';

import { cartApi } from './api/cart';
import { dealsApi } from './api/deal';
import { salesApi } from './api/sales';
import { usersApi } from './api/users';
import { eventApi } from './api/event';
import { cloverApi } from './api/clover';
import { refundApi } from './api/refund';
import { payoutApi } from './api/payout';
import { themesApi } from './api/themes';
import { fiservApi } from './api/fiserv';
import { productApi } from './api/product';
import { companyApi } from './api/company';
import { payrollApi } from './api/payroll';
import { storagesApi } from './api/storage';
import { authReducer } from './slices/auth';
import { expensesApi } from './api/expenses';
import { supplierApi } from './api/supplier';
import { settingsApi } from './api/settings';
import { carriersApi } from './api/carriers';
import { categoryApi } from './api/category';
import { featuresApi } from './api/features';
import { analyticsApi } from './api/analytics';
import { customersApi } from './api/customers';
import { inventoryApi } from './api/inventory';
import { employeesApi } from './api/employees';
import { loaderReducer } from './slices/loader';
import { quickBooksApi } from './api/quickbooks';
import { accountingApi } from './api/accounting';
import { attributesApi } from './api/attributes';
import { eventOrdersApi } from './api/event-orders';
import { shipStationApi } from './api/ship-station';
import { stockOrdersApi } from './api/stock-orders';
import { researchWebApi } from './api/research-web';
import { appointmentsApi } from './api/appointments';
import { researchMailApi } from './api/research-mail';
import { shippingTierApi } from './api/shipping-tier';
import { cloverDeviceApi } from './api/clover-device';
import { productTypesApi } from './api/product-types';
import { notificationsApi } from './api/notifications';
import { priceApprovalApi } from './api/price-approval';
import { eventExpensesApi } from './api/event-expenses';
import { stockTransfersApi } from './api/stock-transfers';
import { paymentMethodsApi } from './api/payment-methods';
import { commissionLevelApi } from './api/commission-level';
import { receiptSettingsApi } from './api/receipt-settings';
import { deliveryTrackingApi } from './api/delivery-tracking';
import { cloverDeviceReducer } from './slices/clover-devices';
import { notificationsReducer } from './slices/notifications';
import { accountingOverviewApi } from './api/accounting-overview';

const apis = [
  cartApi,
  eventApi,
  usersApi,
  salesApi,
  dealsApi,
  cloverApi,
  refundApi,
  payoutApi,
  themesApi,
  fiservApi,
  productApi,
  companyApi,
  expensesApi,
  carriersApi,
  supplierApi,
  categoryApi,
  settingsApi,
  storagesApi,
  featuresApi,
  customersApi,
  analyticsApi,
  inventoryApi,
  employeesApi,
  attributesApi,
  accountingApi,
  stockOrdersApi,
  eventOrdersApi,
  deliveryTrackingApi,
  shipStationApi,
  shippingTierApi,
  productTypesApi,
  cloverDeviceApi,
  appointmentsApi,
  priceApprovalApi,
  eventExpensesApi,
  notificationsApi,
  stockTransfersApi,
  paymentMethodsApi,
  receiptSettingsApi,
  commissionLevelApi,
  quickBooksApi,
  payrollApi,
  researchWebApi,
  researchMailApi,
  accountingOverviewApi,
];

const appReducer = combineReducers({
  auth: authReducer,
  loader: loaderReducer,
  cartApi: cartApi.reducer,
  eventApi: eventApi.reducer,
  usersApi: usersApi.reducer,
  salesApi: salesApi.reducer,
  dealsApi: dealsApi.reducer,
  payoutApi: payoutApi.reducer,
  cloverApi: cloverApi.reducer,
  refundApi: refundApi.reducer,
  themesApi: themesApi.reducer,
  fiservApi: fiservApi.reducer,
  productApi: productApi.reducer,
  companyApi: companyApi.reducer,
  payrollApi: payrollApi.reducer,
  featuresApi: featuresApi.reducer,
  expensesApi: expensesApi.reducer,
  carriersApi: carriersApi.reducer,
  supplierApi: supplierApi.reducer,
  categoryApi: categoryApi.reducer,
  settingsApi: settingsApi.reducer,
  storagesApi: storagesApi.reducer,
  cloverDevice: cloverDeviceReducer,
  customersApi: customersApi.reducer,
  analyticsApi: analyticsApi.reducer,
  inventoryApi: inventoryApi.reducer,
  employeesApi: employeesApi.reducer,
  notifications: notificationsReducer,
  attributesApi: attributesApi.reducer,
  accountingApi: accountingApi.reducer,
  quickBooksApi: quickBooksApi.reducer,
  shipStationApi: shipStationApi.reducer,
  stockOrdersApi: stockOrdersApi.reducer,
  eventOrdersApi: eventOrdersApi.reducer,
  researchWebApi: researchWebApi.reducer,
  researchMailApi: researchMailApi.reducer,
  productTypesApi: productTypesApi.reducer,
  shippingTierApi: shippingTierApi.reducer,
  appointmentsApi: appointmentsApi.reducer,
  cloverDeviceApi: cloverDeviceApi.reducer,
  priceApprovalApi: priceApprovalApi.reducer,
  notificationsApi: notificationsApi.reducer,
  eventExpensesApi: eventExpensesApi.reducer,
  stockTransfersApi: stockTransfersApi.reducer,
  paymentMethodsApi: paymentMethodsApi.reducer,
  commissionLevelApi: commissionLevelApi.reducer,
  receiptSettingsApi: receiptSettingsApi.reducer,
  deliveryTrackingApi: deliveryTrackingApi.reducer,
  accountingOverviewApi: accountingOverviewApi.reducer,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === 'auth/clearAuth') {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(apis.map((api) => api.middleware)),
});

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;
